import CustomHtmlKeyboardResponsePlugin from "../../../customPlugin/customHtmlKeyboardResponse";
import { KEY_CODES } from "../../../gearshiftGame/constants";
import { Game } from "../../../gearshiftGame/game";
import { Score } from "../../../gearshiftGame/score";
import { createReadyOverlay, createCongratsOverlay } from "../../ui/overlays";
import { resolveLayoutPlaceholders, applyTutorialHeaderPlaceholders } from "../../../lib/utils";
import { createPreservedFrame, prepareBoardPreview } from "./missionHelpers";

/**
 * Tutorial builder for Mission 3.
 * Replays selected phases using the shared missionThree phase builders.
 * Crash/wrong-lane handling and overlays.
 */
export function buildTutorialWithPhasesMissionThree({
  jsPsych,
  metadata,
  score,
  trialConfig,
  stimulus,
  phases = [0, 1, 2, 3, 4, 5],
  buildersMap = {},
  overlayOptions = {},
  hooks = {},
}) {
  const resolveOverlayText = (text) => resolveLayoutPlaceholders(jsPsych, text);

  const ready = overlayOptions.ready || {};
  const congrats = overlayOptions.congrats || {};

  const readyForward = ready.forwardText ?? `[press "__FORWARD_KEY__" to begin ->]`;
  const readyBack = ready.backText ?? "";
  const readyImage = ready.imageOrVideoUrl ?? "assets/images/instructions/video_placeholder.svg";
  const readyShowText = ready.showText ?? false;

  const congratsForward = congrats.forwardText ?? `[press "__FORWARD_KEY__" to continue ->]`;
  const congratsBack = congrats.backText ?? "";
  const congratsImage = congrats.imageOrVideoUrl ?? "assets/images/instructions/video_placeholder.svg";
  const congratsForwardCrash = congrats.forwardTextCrash ?? `[press "__FORWARD_KEY__" to try again ]`;
  const congratsBackCrash = congrats.backTextCrash ?? "";
  const congratsImageCrash =
    congrats.imageOrVideoUrlCrash ?? "assets/images/instructions/try_again_placeholder.svg";
  const congratsShowText = congrats.showText ?? false;

  const timeline = [];
  const lastPhase = phases[phases.length - 1];
  const firstPhase = phases[0];
  // reset crash/skip flags for this tutorial iteration
  // Note: in Mission 3 the crash can happen on phase 1 and we must skip the remaining phases
  // of the current iteration;
  // Mission 2 is different, its let the flow reach later phases before the overlay.
  window._gear_crashed = false;
  window._gear_stopRemaining = false;
  window._gear_missedBriefcase = false;
  window._gear_preservedFrameHTML = null;
  window._gear_preservedFrameHTML_phaseStart = null;
  window._gear_preservedFrameHTML_phaseStart_len = 0;

  // Helper to create a fresh Game for the current iteration
  function createFreshGame() {
    // Create a temporary Score for the tutorial so it doesn't affect the real Score
    const tutorialScore = new Score(jsPsych, score.startingLives, score.rewardReduction);

    // Create tutorial config without any flag for now
    const tutorialTrialConfig = {
      ...trialConfig,
    };

    // Use tutorialScore instead of SCORE
    window._gear_currentGame = new Game(jsPsych, metadata, tutorialScore, tutorialTrialConfig);
    window._gear_currentGame.isTutorial = true;
    applyTutorialHeaderPlaceholders(jsPsych);
    return window._gear_currentGame;
  }

  // if the first phase is not 0, add a ready overlay at the beginning of other tutorials
  if (firstPhase !== 0) {
    const readyTrial = {
      choices: "NO_KEYS",
      on_load: function () {
        const game = createFreshGame();
        prepareBoardPreview(game, firstPhase);

        const overlay = createReadyOverlay({
          forwardText: resolveOverlayText(readyForward),
          backText: resolveOverlayText(readyBack),
          imageOrVideoUrl: readyImage,
          showText: readyShowText,
          onStart: () => {
            try {
              game.startPhase0();
              if (firstPhase >= 2) {
                game.matchDriverAndTargetSpeed();
                if (typeof game.updateCoordinates === "function") {
                  game.updateCoordinates();
                }
              }
            } catch (e) {
              console.warn("Failed to initialize board:", e);
            }
            overlay.remove();
            jsPsych.finishTrial();
          },
        });

        document.body.appendChild(overlay);
      },
      on_finish: function (data) {
        data.name = "readyOverlay";
      },
    };
    timeline.push(readyTrial);
  }

  for (const p of phases) {
    const builder = buildersMap[p];
    if (!builder) continue;

    if (p === 0) {
      const phase0Trial = {
        choices: "NO_KEYS",
        on_load: function () {
          const game = createFreshGame();
          prepareBoardPreview(game, 0);

          const overlay = createReadyOverlay({
            forwardText: resolveOverlayText(readyForward),
            backText: resolveOverlayText(readyBack),
            imageOrVideoUrl: readyImage,
            showText: readyShowText,
            onStart: () => {
              try {
                game.startPhase0();
              } catch (e) {
                console.error("Error starting phase0:", e);
              }

              const phaseTimeout = setTimeout(() => {
                jsPsych.finishTrial();
              }, game.targetLaunchDelay);

              window._gear_phase0_timeout = function () {
                if (phaseTimeout) clearTimeout(phaseTimeout);
              };
            },
          });

          document.body.appendChild(overlay);
        },
        on_finish: function (data) {
          try {
            if (p === lastPhase) {
              const display = document.querySelector(".jspsych-display-element") || document.body;
              const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
              document.body.appendChild(preserved);
            }
          } catch (e) {
            console.warn(
              "buildTutorialWithPhasesMissionThree: failed to preserve final frame (phase0):",
              e
            );
          }

          try {
            const clearFn = window._gear_phase0_timeout;
            if (typeof clearFn === "function") clearFn();
          } catch (e) {
            console.warn("buildTutorialWithPhasesMissionThree: error clearing phase0 timeout:", e);
          }

          try {
            const game = window._gear_currentGame;
            data.name = "initializeTarget";
            if (game) {
              data.driverSpeed = game.driverCarSpeed;
              data.distanceToTarget = game.driverDistanceToTarget;
              data.trialStart = {
                name: "trialStart",
                driverSpeed: game.driverCarSpeed,
                timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
              };
            }
          } catch (e) {
            console.warn(
              "buildTutorialWithPhasesMissionThree: error assigning phase0 finish data:",
              e
            );
          }
          if (hooks.onPhaseFinish) hooks.onPhaseFinish(p, data, window._gear_currentGame);
        },
      };

      timeline.push(phase0Trial);
      continue;
    }

    // for other phases, construct trials that use `window._gear_currentGame`
    const phaseTrials = builder({}, jsPsych, score);
    for (const t of phaseTrials) {
      const cloned = Object.assign({}, t);

      // Set trial_duration for specific phases
      if (p === 2) {
        cloned.trial_duration = function () {
          return window._gear_currentGame ? window._gear_currentGame.csi : 0;
        };
      }

      // Replace on_load with a function that calls the correct startPhase
      const parsedOnLoad = function () {
        applyTutorialHeaderPlaceholders(jsPsych);
        try {
          if (!window._gear_currentGame) createFreshGame();
          const game = window._gear_currentGame;

          // capture snapshot at phase start for fallback
          try {
            const display = document.querySelector(".jspsych-display-element") || document.body;
            const html = display.innerHTML;
            const len = html?.length || 0;
            if (len >= 200) {
              window._gear_preservedFrameHTML_phaseStart = html;
              window._gear_preservedFrameHTML_phaseStart_len = len;
            }
          } catch (e) {
            console.warn(
              "buildTutorialWithPhasesMissionThree: failed to capture phase start snapshot",
              e
            );
          }
          switch (p) {
            case 1:
              game.startPhase1();
              break;
            case 2:
              game.startPhase2();
              break;
            case 3:
              game.startPhase3_M3();
              break;
            case 4:
              // phase 4 needs the chosen key from previous trial
              try {
                const last = jsPsych.data.get().last(1).values()[0] || {};
                let keyCode = last.keyCode;
                if (keyCode === undefined || keyCode === null || keyCode === 0) {
                  keyCode = KEY_CODES["d"] ?? 0;
                }
                game.startPhase4(keyCode);
              } catch (e) {
                console.warn("Phase 4 fallback failed, starting with default lane:", e);
                try {
                  const fallbackKey = KEY_CODES["d"] ?? 0;
                  game.startPhase4(fallbackKey);
                } catch (ee) {
                  console.warn("Phase 4 default start failed:", ee);
                }
              }
              break;
            case 5:
              // phase 5 needs the chosen key from 2 trials ago
              try {
                const last = jsPsych.data.get().last(2).values()[0] || {};
                let keyCode = last.keyCode;
                if (keyCode === undefined || keyCode === null || keyCode === 0) {
                  keyCode = KEY_CODES["d"] ?? 0;
                }
                game.startPhase5(keyCode);
              } catch (e) {
                console.warn("Phase 5 fallback failed, starting with default lane:", e);
                try {
                  const fallbackKey = KEY_CODES["d"] ?? 0;
                  game.startPhase5(fallbackKey);
                } catch (ee) {
                  console.warn("Phase 5 default start failed:", ee);
                }
              }
              break;
            case 7:
              game.startPhase7();
              break;
            default:
              break;
          }
          if (hooks.onPhaseStart) hooks.onPhaseStart(p, game);
        } catch (e) {
          console.warn(`Error starting phase ${p}:`, e);
        }
      };

      cloned.on_load = parsedOnLoad;

      // on_finish use runtime game state and preserve frame and crash flags
      cloned.on_finish = function (data) {
        try {
          // Preserve final frame of last phase
          if (p === lastPhase) {
            const display = document.querySelector(".jspsych-display-element") || document.body;
            const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
            const snapshotLength = preserved.innerHTML?.length || 0;
            const previousLength = window._gear_preservedFrameHTML
              ? window._gear_preservedFrameHTML.length
              : 0;
            if (snapshotLength < 200) {
              if (window._gear_preservedFrameHTML_phaseStart_len >= 200) {
                const fallbackPreserved = createPreservedFrame({
                  html: window._gear_preservedFrameHTML_phaseStart,
                  display,
                  score,
                });
                const existing = document.getElementById("gear-preserved-frame");
                if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
                document.body.appendChild(fallbackPreserved);
                window._gear_preservedFrameHTML = fallbackPreserved.innerHTML;
              }
            } else {
              const existing = document.getElementById("gear-preserved-frame");
              if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
              document.body.appendChild(preserved);
              if (snapshotLength >= previousLength) {
                window._gear_preservedFrameHTML = preserved.innerHTML;
              }
            }
          }
          if (p === 1 && data && data.crashed) {
            console.log("Mission Three Tutorial: Crash detected in phase 1! Preserving frame...");
            try {
              window._gear_crashed = true;
              const display = document.querySelector(".jspsych-display-element") || document.body;
              if (!document.getElementById("gear-preserved-frame")) {
                const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
                document.body.appendChild(preserved);
              }
            } catch (e) {
              console.warn(
                "buildTutorialWithPhasesMissionThree: failed to preserve frame on crash:",
                e
              );
            }
          } else if (p === 1) {
            console.log("Mission Three Tutorial: Phase 1 finished. data.crashed =", data.crashed);
          }
          // Phase-specific data assignments using the runtime game instance
          const game = window._gear_currentGame;
          if (game) {
            switch (p) {
              case 1:
                data.name = "engageTarget";
                data.cueCarOn = {
                  name: "cueCarOn",
                  driverSpeed: game.driverCarSpeed,
                  timestamp: jsPsych.data.get().last(1).values()[0].time_elapsed,
                  distanceToTarget: game.driverDistanceToTarget,
                  csi: game.csi,
                };
                break;
              case 2:
                data.name = "licensePlateOn";
                data.timestamp = jsPsych.data.get().last(2).values()[0].time_elapsed;
                data.driverSpeed = game.driverCarSpeed;
                data.distanceToTarget = jsPsych.data.get().last(2).values()[0].distanceToTarget;
                break;
              case 3: {
                data.name = "responseKeyPressed";
                data.response_keypress_time = data.time_elapsed;
                data.keyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null;
                data.responseTime =
                  data.time_elapsed - jsPsych.data.get().last(2).values()[0].time_elapsed;
                data.driverSpeed = game.driverCarSpeed;
                // displayLicense()/captureLicensePlateOnTime() only run once startPhase3_M3's
                // on_load fires, which is after the "licensePlateOn" (phase 2) row already
                // saved - so this is the earliest row where the value is actually available
                data.license_plate_on_time = game.licensePlateOnTime;

                // update the briefcase flags
                const grabbedMysteryBriefcase = game.escapism && data.keyCode === 1;
                const missedBriefcase = game.escapism && data.keyCode !== 1;
                window._gear_mysteryBriefcaseGrabbed = grabbedMysteryBriefcase;
                window._gear_missedBriefcase = missedBriefcase;
                break;
              }
              case 4:
                data.name = "driverInResponseLane";
                data.driverSpeed = game.driverCarSpeed;
                break;
              case 5:
                data.name = "packageDisappears";
                data.driverSpeed = game.driverCarSpeed;
                data.emotion = game.emotion;
                data.packageInTargetLane = {
                  timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
                  driverSpeed: game.driverCarSpeed,
                  packageDistance: game.packageDistanceToTarget,
                };
                data.targetDisappears = {
                  timestamp: game.targetCarDisappearTimestamp,
                  driverSpeed: game.driverCarSpeed,
                };
                data.emotionReaction = {
                  emotion: game.emotion,
                  driverSpeed: game.driverCarSpeed,
                  timestamp:
                    jsPsych.data.get().last(1).values()[0].time_elapsed - game.feedbackDuration,
                };
                break;
              case 7:
                data.name = "driverAtCenter";
                data.driverToCenter = {
                  timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
                  driverSpeed: game.driverCarSpeed,
                };
                data.driverSpeed = game.driverCarSpeed;
                break;
              default:
                break;
            }
          }

          // If crashed on phase 1, mark to skip remaining phases in this iteration
          if (p === 1 && data && data.crashed) {
            window._gear_stopRemaining = true;
          }
        } catch (e) {
          console.warn(`buildTutorialWithPhasesMissionThree: error in phase ${p} on_finish:`, e);
        }

        if (hooks.onPhaseFinish) hooks.onPhaseFinish(p, data, window._gear_currentGame);
      };
      // Skip remaining phases in this iteration when a crash was detected on phase 1
      // Mission 3 needs this guard because phase 1 crash should jump straight to the overlay
      // (Mission 2 allows later phases to run before showing the overlay).
      if (p !== firstPhase) {
        timeline.push({
          timeline: [cloned],
          conditional_function: function () {
            return !window._gear_stopRemaining;
          },
        });
      } else {
        timeline.push(cloned);
      }
    }
  }

  // create a small congrats trial that displays an overlay
  const congratsTrial = {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus: "",
    choices: "NO_KEYS",
    on_load: () => {
      const pres = document.getElementById("gear-preserved-frame");
      if (!pres) return;

      // get the crash state and missed briefcase state
      const crashed = !!window._gear_crashed;
      const missedBriefcase = !!window._gear_missedBriefcase;
      const failed = crashed || missedBriefcase;
      // conditional strings/images
      const forwardText = failed ? congratsForwardCrash : congratsForward;
      const backText = failed ? congratsBackCrash : congratsBack;
      const imageOrVideoUrl = failed ? congratsImageCrash : congratsImage;

      // Check fails limit before showing overlay
      if (hooks.onBeforeCongratsOverlay) {
        try {
          const shouldInterrupt = hooks.onBeforeCongratsOverlay({
            tryAgain: failed,
            crashed,
            missedBriefcase,
          });
          if (shouldInterrupt === true) {
            return; // Stop execution, don't show overlay
          }
        } catch (e) {
          console.warn("buildTutorialWithPhasesMissionThree: onBeforeCongratsOverlay failed:", e);
        }
      }

      const overlay = createCongratsOverlay({
        forwardText: resolveOverlayText(forwardText),
        backText: resolveOverlayText(backText),
        imageOrVideoUrl,
        playCelebrate: !failed,
        showText: congratsShowText,
        onContinue: () => {
          // remove preserved frame if present
          const preserved = document.getElementById("gear-preserved-frame");
          if (preserved) preserved.parentNode.removeChild(preserved);

          // clear any timeouts
          if (typeof window._gear_phase0_timeout === "function") window._gear_phase0_timeout();

          // finish the congrats trial, the loop_function will repeat the phases automatically
          // if the last iteration contained a crash.
          jsPsych.finishTrial();
        },
      });

      document.body.appendChild(overlay);
    },
    on_finish: (data) => {
      data.name = "congratsOverlay";
      if (hooks.onPhaseFinish) hooks.onPhaseFinish("congrats", data, window._gear_currentGame);
    },
  };

  timeline.push(congratsTrial);

  // Wrap the phases timeline in a looping node which will repeat the entire
  // set of phases when a crash was detected
  const node = {
    timeline: [
      {
        type: CustomHtmlKeyboardResponsePlugin,
        stimulus,
        data: { trialConfig, tutorial: true },
        timeline,
      },
    ],
    loop_function: function (data) {
      // if any trial in the previous iteration flagged `crashed`, repeat.
      try {
        const values = data.values();
        const crashed = values.some((v) => !!v.crashed);
        const missedBriefcase = !!window._gear_missedBriefcase;
        let shouldRepeat = crashed || missedBriefcase;

        // clear the per-iteration stop flag
        window._gear_stopRemaining = false;

        if (hooks.onLoopDecide) {
          try {
            const maybe = hooks.onLoopDecide({
              data,
              crashed,
              defaultShouldRepeat: shouldRepeat,
            });
            if (typeof maybe === "boolean") shouldRepeat = maybe;
          } catch (e) {
            console.warn("buildTutorialWithPhasesMissionThree: onLoopDecide failed:", e);
          }
        }

        // clear the global flags so the next iteration starts clean
        window._gear_crashed = false;
        window._gear_missedBriefcase = false;

        return shouldRepeat;
      } catch (e) {
        console.warn("buildTutorialWithPhasesMissionThree: loop_function error:", e);
        return false;
      }
    },
  };

  return node;
}
